// Mark: Used in V2
import { Link as GatsbyLink } from "gatsby"
import React, { useMemo } from "react"

import { linkIsExternalUrl, getUrlFromStoryblokLink } from "@utils/storyblok"
import { appendQueryParams } from "@utils/url"

export type CTATrackingEvent = {
  trackingEvent: string
  trackingEventKey?: string
  trackingEventValue?: string
}

type Props = React.HTMLAttributes<HTMLAnchorElement> & {
  children: any
  link: string | Storyblok.Link
  queryParams?: string
  opensNewTab?: boolean
  onClick?: React.MouseEventHandler<HTMLElement>
  className?: string
  "aria-label"?: string
  onMouseEnter?: React.MouseEventHandler<HTMLElement>
  onFocus?: React.FocusEventHandler<HTMLElement>
  "data-testid"?: string
}

/*
  Usage note: This Link element should be used to augment
  another element or component to turn it into a link where
  we cannot predict whether the link target of the link will
  be internal or external. E.g. an image or custom card may be
  wrapped in this Link.
*/
const Link = React.forwardRef<HTMLAnchorElement, Props>(
  (
    {
      children,
      link,
      queryParams,
      opensNewTab = true,
      onClick,
      className,
      "aria-label": ariaLabel,
      onMouseEnter,
      onFocus,
      "data-testid": dataTestId,
      ...props
    },
    ref
  ) => {
    const href = useMemo(() => {
      if (link) {
        let url =
          typeof link === "string" ? link : getUrlFromStoryblokLink(link)
        if (queryParams) {
          url = appendQueryParams(url, queryParams)
        }
        return url
      }

      return undefined
    }, [link, queryParams])

    if (linkIsExternalUrl(link)) {
      if (opensNewTab) {
        return (
          <a
            href={href}
            target="_blank"
            rel="noreferrer nofollow"
            ref={ref as React.Ref<HTMLAnchorElement>}
            className={className}
            onClick={onClick}
            aria-label={ariaLabel}
            onMouseEnter={onMouseEnter}
            onFocus={onFocus}
            data-testid={dataTestId}
            {...props}
          >
            {children}
          </a>
        )
      } else {
        return (
          <a
            href={href}
            ref={ref as React.Ref<HTMLAnchorElement>}
            className={className}
            onClick={onClick}
            aria-label={ariaLabel}
            onMouseEnter={onMouseEnter}
            onFocus={onFocus}
            data-testid={dataTestId}
            {...props}
          >
            {children}
          </a>
        )
      }
    }

    return (
      // TODO - fix any type
      <GatsbyLink
        to={href || "/"}
        ref={ref as React.Ref<any>}
        className={className}
        onClick={onClick}
        aria-label={ariaLabel}
        onMouseEnter={onMouseEnter}
        onFocus={onFocus}
        data-testid={dataTestId}
        {...props}
      >
        {children}
      </GatsbyLink>
    )
  }
)

Link.displayName = "Link"

export default React.memo(Link)
